import React, { useState, ChangeEvent, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import logoImage from '../../../assets/apexlogo.svg';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Toaster } from 'sonner';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// --- Helper Components ---

// Tournament Progress Bar Component
interface TournamentProgressProps {
    currentTeams: number;
    maxTeams: number;
    isLoading?: boolean;
}

const TournamentProgress = ({ currentTeams, maxTeams, isLoading = false }: TournamentProgressProps) => {
    const progressPercentage = Math.min((currentTeams / maxTeams) * 100, 100);
    const spotsRemaining = Math.max(maxTeams - currentTeams, 0);
    
    return (
        <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Tournament Registration Progress
                </h3>
                <div className="text-right">
                    <div className="text-2xl font-bold text-red-400">
                        {isLoading ? (
                            <div className="animate-pulse bg-gray-600 h-8 w-16 rounded"></div>
                        ) : (
                            `${currentTeams}/${maxTeams}`
                        )}
                    </div>
                    <div className="text-sm text-gray-400">
                        {isLoading ? (
                            <div className="animate-pulse bg-gray-600 h-4 w-20 rounded mt-1"></div>
                        ) : spotsRemaining > 0 ? (
                            `${spotsRemaining} spots left`
                        ) : (
                            "Tournament Full"
                        )}
                    </div>
                </div>
            </div>
            
            <div className="relative">
                <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden">
                    <div 
                        className={`h-full transition-all duration-700 ease-out ${
                            progressPercentage >= 100 
                                ? 'bg-gradient-to-r from-yellow-500 to-red-500 shadow-[0_0_15px_rgba(239,68,68,0.6)]' 
                                : progressPercentage >= 80 
                                ? 'bg-gradient-to-r from-orange-500 to-red-500 shadow-[0_0_10px_rgba(239,68,68,0.4)]'
                                : 'bg-gradient-to-r from-green-500 to-red-500 shadow-[0_0_8px_rgba(239,68,68,0.3)]'
                        }`}
                        style={{ width: `${progressPercentage}%` }}
                    ></div>
                </div>
                
                <div className="flex justify-between text-xs text-gray-400 mt-2">
                    <span>0 teams</span>
                    <span className="text-red-400 font-medium">{maxTeams} teams (Full)</span>
                </div>
            </div>
            
            {progressPercentage >= 100 && (
                <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <span className="text-yellow-400 font-medium">Tournament is full! New registrations will be added to the waitlist.</span>
                    </div>
                </div>
            )}
        </div>
    );
};

// FormInput props interface
interface FormInputProps {
    id: string;
    label: string;
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    placeholder: string;
    type?: string;
}

// A reusable styled input component
const FormInput = ({ id, label, value, onChange, placeholder, type = "text" }: FormInputProps) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-2">
            {label}
        </label>
        <input
            type={type}
            id={id}
            name={id}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className="w-full bg-gray-900/50 border-2 border-gray-700 rounded-lg text-white px-4 py-3 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition duration-300"
        />
    </div>
);

// A cool, glowing divider line with custom logo
const GlowDivider = ({ logo, logoSize = "w-8 h-8", logoColor = "text-red-500" }: { logo?: React.ReactNode; logoSize?: string; logoColor?: string }) => (
    <div className="relative my-6">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-red-500/30 shadow-[0_0_10px_rgba(239,68,68,0.3)]" />
        </div>
        <div className="relative flex justify-center">
            <span className="bg-gray-800 px-4 py-2 rounded-full border-2 border-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.4)] hover:shadow-[0_0_20px_rgba(239,68,68,0.6)] transition-all duration-300">
                {logo ? (
                    <div className={`${logoSize} ${logoColor} flex items-center justify-center`}>
                        {logo}
                    </div>
                ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" className={logoColor}>
                        <path d="M8 1.05a7 7 0 0 1 4.793 12.023l-1.558.877a5.5 5.5 0 1 0-6.47 0l-1.558-.877A7 7 0 0 1 8 1.05z"/>
                    </svg>
                )}
            </span>
        </div>
    </div>
);

// Sortable Map Item Component
interface SortableMapItemProps {
    id: string;
    map: string;
    index: number;
    onMoveUp: () => void;
    onMoveDown: () => void;
    totalItems: number;
}

const SortableMapItem = ({ id, map, index, onMoveUp, onMoveDown, totalItems }: SortableMapItemProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="bg-gray-700/50 border border-gray-600 rounded-lg p-4 mb-3 hover:bg-gray-600/50 transition-colors duration-200 hover:border-red-500/50 hover:shadow-[0_0_10px_rgba(239,68,68,0.2)]"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3" {...attributes} {...listeners}>
                    <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center text-red-400 font-bold text-sm cursor-grab">
                        {index + 1}
                    </div>
                    <span className="text-white font-medium">{map}</span>
                </div>
                <div className="flex items-center space-x-2">
                    <div className="text-gray-400 text-sm hidden sm:block">
                        Priority {index + 1}
                    </div>
                    <div className="flex flex-col space-y-1 ml-2">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveUp();
                            }}
                            disabled={index === 0}
                            className={`p-1 rounded ${index === 0 ? 'text-gray-600 cursor-not-allowed' : 'text-red-400 hover:text-red-300 hover:bg-gray-600/50'}`}
                            aria-label="Move up"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveDown();
                            }}
                            disabled={index === totalItems - 1}
                            className={`p-1 rounded ${index === totalItems - 1 ? 'text-gray-600 cursor-not-allowed' : 'text-red-400 hover:text-red-300 hover:bg-gray-600/50'}`}
                            aria-label="Move down"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Map Preference Board Component
interface MapPreferenceBoardProps {
    maps: { id: string; name: string; }[];
    onMapsChange: (maps: { id: string; name: string; }[]) => void;
}

const MapPreferenceBoard = ({ maps, onMapsChange }: MapPreferenceBoardProps) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: { active: { id: string | number }; over: { id: string | number } | null }): void => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = maps.findIndex((map) => map.id === String(active.id));
            const newIndex = maps.findIndex((map) => map.id === String(over.id));

            const newMaps = arrayMove(maps, oldIndex, newIndex);
            onMapsChange(newMaps);
        }
    };

    const moveItem = (currentIndex: number, direction: 'up' | 'down') => {
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex >= 0 && newIndex < maps.length) {
            const newMaps = arrayMove([...maps], currentIndex, newIndex);
            onMapsChange(newMaps);
        }
    };

    return (
        <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Map Preference Ranking
            </h4>
            <p className="text-gray-400 text-sm mb-6">Drag and drop or use the arrow buttons to rank your team's map preferences. Top map has highest priority.</p>
            
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={maps.map(map => map.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                        {maps.map((map, index) => (
                            <SortableMapItem
                                key={map.id}
                                id={map.id}
                                map={map.name}
                                index={index}
                                onMoveUp={() => moveItem(index, 'up')}
                                onMoveDown={() => moveItem(index, 'down')}
                                totalItems={maps.length}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    );
};

// Form Errors Interface
interface FormErrors {
    [key: string]: string;
}

// Map Item Interface
interface MapItem {
    id: string;
    name: string;
}

// --- Main Registration Form Component ---

function RegistrationForm({ onSuccessfulSubmit }: { onSuccessfulSubmit: () => void }) {
    const [squadName, setSquadName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>(''); 
    const [players, setPlayers] = useState<string[]>(Array(5).fill(''));
    const [errors, setErrors] = useState<FormErrors>({});
    
    // Map preferences state
    const [mapPreferences, setMapPreferences] = useState<MapItem[]>([
        { id: 'map1', name: 'Lotus' },
        { id: 'map2', name: 'Bind' },
        { id: 'map3', name: 'Abyss' },
        { id: 'map4', name: 'Haven' },
        { id: 'map5', name: 'Split' },
        { id: 'map6', name: 'Ascent' },
        { id: 'map7', name: 'Icebox' },
        { id: 'map8', name: 'Breeze' },
        { id: 'map9', name: 'Fracture' },
        { id: 'map10', name: 'Pearl' },
        { id: 'map11', name: 'Sunset' },
        { id: 'map12', name: 'Corrode' },
    ]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [isWaitlisted, setIsWaitlisted] = useState<boolean>(false);
    const [currentTeamCount, setCurrentTeamCount] = useState<number>(0);
    const [isLoadingTeamCount, setIsLoadingTeamCount] = useState<boolean>(true);
    
    const MAX_TEAMS = 16;

    const handlePlayerChange = (index: number, value: string): void => {
        const newPlayers = [...players];
        newPlayers[index] = value;
        setPlayers(newPlayers);
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        if (!squadName.trim()) newErrors.squadName = "Squad Name is required.";
        if (!email.trim()) {
            newErrors.email = "Contact Email is required.";
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = "Email address is invalid.";
        }
        if (!phoneNumber.trim()) {
            newErrors.phoneNumber = "Phone number is required.";
        } else if (!/^\+?[\d\s-]{10,}$/.test(phoneNumber.trim())) {
            newErrors.phoneNumber = "Please enter a valid phone number.";
        }
        players.forEach((player, index) => {
            if (!player.trim()) {
                newErrors[`player${index}`] = `Player ${index + 1}'s username is required.`;
            } else if (!/^[^#]+#[^#]+$/.test(player.trim())) {
                newErrors[`player${index}`] = `Player ${index + 1}'s username must be in the format 'username#tag' (e.g., 'Sidsmartz#him')`;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const fetchTeamCount = async (): Promise<number> => {
        const { count, error } = await supabase
            .from('squads')
            .select('*', { count: 'exact', head: true });
            
        if (error) {
            console.error('Error fetching team count:', error);
            return 0;
        }
        
        return count || 0;
    };
    
    const checkTeamLimit = async (): Promise<{ canRegister: boolean; isWaitlisted: boolean }> => {
        const currentSquadCount = await fetchTeamCount();
        setCurrentTeamCount(currentSquadCount);
        
        console.log(`[Form] Current squad count: ${currentSquadCount}/${MAX_TEAMS}`);
        
        return {
            canRegister: currentSquadCount < MAX_TEAMS,
            isWaitlisted: currentSquadCount >= MAX_TEAMS
        };
    };
    
    // Load team count on component mount
    useEffect(() => {
        const loadTeamCount = async () => {
            setIsLoadingTeamCount(true);
            try {
                const count = await fetchTeamCount();
                setCurrentTeamCount(count);
            } catch (error) {
                console.error('Error loading team count:', error);
            } finally {
                setIsLoadingTeamCount(false);
            }
        };
        
        loadTeamCount();
    }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        console.log('[Form] Submission started');
        
        if (!validateForm()) {
            console.warn('[Form] Validation failed:', errors);
            return;
        }

        setIsLoading(true);
        const startTime = performance.now();
        
        try {
            // Check team limit first
            const { canRegister, isWaitlisted } = await checkTeamLimit();
            
            if (isWaitlisted) {
                // Add to waitlist
                const { error: waitlistError } = await supabase
                    .from('waitlist')
                    .insert([{
                        team_name: squadName,
                        captain_email: email,
                        captain_phone: phoneNumber,
                        players: players.filter(p => p.trim() !== ''),
                        created_at: new Date().toISOString()
                    }]);
                
                if (waitlistError) throw waitlistError;
                
                console.log('[Form] Team added to waitlist');
                toast.success('Registration is full! Your team has been added to the waitlist. We\'ll contact you if a spot opens up.');
                setIsWaitlisted(true);
                setIsSuccess(true);
                onSuccessfulSubmit();
                return;
            }
            
            // If we get here, registration is open
            // First, check if email is already registered
            const { data: existingTeam, error: checkError } = await supabase
                .from('squads')
                .select('id')
                .eq('captain_email', email)
                .single();

            if (existingTeam) {
                throw new Error('This email is already registered with another team');
            }

            // If email is not registered, proceed with submission
            // First, create a team in the teams table
            const { data: teamData, error: teamError } = await supabase
                .from('teams')
                .insert([{
                    name: squadName,
                    captain_email: email,
                    created_at: new Date().toISOString()
                }])
                .select()
                .single();

            if (teamError) {
                console.error('[Team Creation Error]', teamError);
                throw new Error('Failed to create team: ' + teamError.message);
            }
            
            // Prepare squad data
            const squadData = {
                squad_name: squadName,
                captain_email: email,
                captain_phone_number: phoneNumber,
                team_id: teamData.id,
                player1_username: players[0] || '',
                player2_username: players[1] || '',
                player3_username: players[2] || '',
                player4_username: players[3] || '',
                player5_username: players[4] || '',
                map_preferences: mapPreferences.map((map, index) => ({
                    id: map.id,
                    name: map.name,
                    rank: index + 1
                }))
            };
            
            console.log('[Form] Submitting squad data:', JSON.stringify(squadData, null, 2));
            
            // Create the squad with the team_id
            const { data, error, status } = await supabase
                .from('squads')
                .insert([squadData])
                .select();

            const endTime = performance.now();
            const duration = (endTime - startTime).toFixed(2);
            
            if (error) {
                console.error(`[Supabase] ❌ Error (${status}):`, error);
                throw error;
            }
            
            console.log(`[Supabase] ✅ Success! Operation took ${duration}ms`);
            console.log('[Supabase] Inserted data:', data);
            
            toast.success('Registration successful! Your squad is now registered for the tournament.');
            console.log('[Form] Registration successful');
            setIsWaitlisted(false);
            setIsSuccess(true);
            onSuccessfulSubmit();
            
        } catch (error) {
            console.error('[Form] Submission failed:', error);
            
            // Handle different error types
            if (error instanceof Error) {
                console.error('[Error] Message:', error.message);
                
                // Check if it's a Supabase error
                if ('code' in error) {
                    const supabaseError = error as { code?: string; details?: string; hint?: string; message: string };
                    console.error('[Supabase] Error Code:', supabaseError.code);
                    console.error('[Supabase] Error Details:', supabaseError.details);
                    toast.error(supabaseError.message || 'A database error occurred');
                } else {
                    toast.error(error.message || 'An error occurred during registration');
                }
            } else {
                toast.error('An unknown error occurred');
            }
        } finally {
            setIsLoading(false);
            console.log('[Form] Submission process completed');
        }
    };

    if (isSuccess) {
        return (
            <div className={`w-full max-w-lg text-center bg-gray-800/80 backdrop-blur-sm rounded-lg p-8 ${
                isWaitlisted 
                    ? 'border-2 border-yellow-500 shadow-[0_0_20px_rgba(234,179,8,0.3)]' 
                    : 'border-2 border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.3)]'
            }`}>
                <h2 className="text-3xl font-bold mb-2">
                    {isWaitlisted ? 'Waitlisted!' : 'All set!'}
                </h2>
                <p className="text-gray-300 mb-6">
                    {isWaitlisted ? (
                        <>
                            All tournament slots are currently full. Your squad, <span className="font-bold text-yellow-400">{squadName}</span>, has been added to the waitlist.
                            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                                <p className="text-yellow-400">
                                    We'll notify you at <span className="font-semibold">{email}</span> if a spot opens up!
                                </p>
                            </div>
                        </>
                    ) : (
                        <>Your squad, <span className="font-bold text-green-400">{squadName}</span>, is now registered for the tournament!</>
                    )}
                </p>
                <button 
                    onClick={() => {
                        onSuccessfulSubmit();
                        // Reset form state when closing
                        setIsSuccess(false);
                        setIsWaitlisted(false);
                    }}
                    className={`mt-4 ${
                        isWaitlisted 
                            ? 'bg-yellow-600 hover:bg-yellow-700' 
                            : 'bg-green-600 hover:bg-green-700'
                    } text-white font-bold py-2 px-6 rounded-lg transition-colors`}
                >
                    {isWaitlisted ? 'Got it!' : 'Close'}
                </button>
            </div>
        );
    }

    return (
        <div className="bg-black text-white min-h-screen flex items-center justify-center p-4 font-sans" 
             style={{ background: 'radial-gradient(circle at top, #1a0000 0%, #000000 40%)' }}>
            <Toaster position="top-center" richColors closeButton />
            <div className="w-full max-w-2xl bg-gray-800/50 backdrop-blur-sm border-2 border-red-500/50 rounded-lg p-8 shadow-[0_0_30px_rgba(239,68,68,0.3)]">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-red-500 tracking-wider uppercase">Register your squad!</h1>
                    <p className="text-gray-400 mt-2">Assemble your squad and enter the arena.</p>
                </div>

                <TournamentProgress 
                    currentTeams={currentTeamCount} 
                    maxTeams={MAX_TEAMS} 
                    isLoading={isLoadingTeamCount}
                />

                <form onSubmit={handleSubmit} noValidate>
                    <div className="space-y-6">
                        <h3 className="text-xl font-semibold text-white border-l-4 border-red-500 pl-4">Team Details</h3>
                        <FormInput id="squadName" label="Squad Name" value={squadName} onChange={(e) => setSquadName(e.target.value)} placeholder="e.g., Red Flowers" />
                        {errors.squadName && <p className="text-red-400 text-sm -mt-4">{errors.squadName}</p>}

                        <FormInput id="email" label="Team Captain's Email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="captain@example.com" type="email" />
                        {errors.email && <p className="text-red-400 text-sm -mt-4">{errors.email}</p>}

                        <FormInput 
                            id="phoneNumber" 
                            label="Team Captain's Phone Number" 
                            value={phoneNumber} 
                            onChange={(e) => setPhoneNumber(e.target.value)} 
                            placeholder="+91 234 567 8900" 
                            type="tel" 
                        />
                        {errors.phoneNumber && <p className="text-red-400 text-sm -mt-4">{errors.phoneNumber}</p>}

                        <GlowDivider 
                            logo={
                                <img 
                                    src={logoImage} 
                                    alt="Logo" 
                                    className="w-full h-full object-contain"
                                />
                            }
                            logoSize="w-12 h-12"
                            logoColor="text-red-400"
                        />

                        <div className="mb-4">
                            <h3 className="text-xl font-semibold text-white border-l-4 border-red-500 pl-4 mb-2">Player Roster (5 Members)</h3>
                            <p className="text-sm text-gray-400 pl-4 mb-4">Enter usernames in the format: username#tag (e.g., Sidsmartz#him)</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                            {players.map((player, index) => (
                                <div key={index}>
                                    <input
                                        type="text"
                                        className={`w-full bg-gray-900/50 border-2 ${
                                            errors[`player${index}`] ? 'border-red-500' : 'border-gray-700'
                                        } rounded-lg text-white px-4 py-3 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition duration-300`}
                                        value={player}
                                        onChange={(e) => handlePlayerChange(index, e.target.value)}
                                        placeholder={`Player ${index + 1} Username`}
                                    />
                                    {errors[`player${index}`] && (
                                        <p className="mt-1 text-sm text-red-400">{errors[`player${index}`]}</p>
                                    )}
                                </div>
                            ))}
                        </div>

                        <h3 className="text-xl font-semibold text-white border-l-4 border-red-500 pl-4">Map Preferences</h3>
                        <MapPreferenceBoard 
                            maps={mapPreferences} 
                            onMapsChange={setMapPreferences} 
                        />

                        <div className="pt-6">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full flex justify-center items-center bg-red-600 text-white font-bold text-lg py-4 px-6 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-4 focus:ring-red-500/50 transition-all duration-300 ease-in-out disabled:bg-gray-600 disabled:cursor-not-allowed group"
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        Register Squad
                                        <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">&rarr;</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default RegistrationForm;